import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ScrollView, StyleSheet, Switch, Text, View } from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppInput } from '../../components/AppInput';
import { AppScreen } from '../../components/AppScreen';
import { MultiSelectEntityPicker } from '../../components/MultiSelectEntityPicker';
import { ScreenHeader } from '../../components/ScreenHeader';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAppColors } from '../../context/PreferencesContext';
import { getAllOrganizationEmployees } from '../../services/employeeService';
import {
  createHolidayCalendar,
  getEmployeeCalendarAssignments,
  getHolidayCalendar,
  updateHolidayCalendar,
} from '../../services/holidayCalendarService';
import type { EmployeeListItem } from '../../types/employee';
import type {
  EmployeeCalendarAssignment,
  HolidayCalendarEmployee,
} from '../../types/holidayCalendar';
import { normalizeApiError } from '../../api/apiError';
import { todayBusinessCalendarDate } from '../../utils/dateTime';
import { runOperation } from '../../utils/operationFeedback';

type CreateProps = NativeStackScreenProps<RootStackParamList, 'HolidayCalendarCreate'>;
type EditProps = NativeStackScreenProps<RootStackParamList, 'HolidayCalendarEdit'>;
type Props = CreateProps | EditProps;

type PickerEmployee = {
  employeeId: number;
  employeeNumber: string | null;
  displayName: string;
  username: string | null;
};

type NameSource = {
  employeeId: number;
  employeeNumber?: string | null;
  displayName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  username?: string | null;
};

function isEditRoute(props: Props): props is EditProps {
  return props.route.name === 'HolidayCalendarEdit';
}

function looksLikeEmployeeNumber(
  value: string | null | undefined,
  employeeNumber: string | null | undefined,
): boolean {
  if (!value?.trim()) return true;
  const text = value.trim();
  const number = employeeNumber?.trim();
  if (number && text.toLowerCase() === number.toLowerCase()) return true;
  return /^e?\d+$/i.test(text);
}

function composedName(item: {
  displayName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
}): string | null {
  const display = item.displayName?.trim() || null;
  if (display) return display;
  const joined = [item.firstName, item.lastName]
    .map(part => part?.trim())
    .filter(Boolean)
    .join(' ')
    .trim();
  return joined || null;
}

/** Prefer a real person name; never use employee number as the primary label. */
function employeeDisplayName(item: {
  displayName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  username?: string | null;
  employeeNumber?: string | null;
  employeeId: number;
}): string {
  const name = composedName(item);
  if (name && !looksLikeEmployeeNumber(name, item.employeeNumber)) {
    return name;
  }
  const username = item.username?.trim() || null;
  if (username && !looksLikeEmployeeNumber(username, item.employeeNumber)) {
    return username;
  }
  return `Employee ${item.employeeId}`;
}

function toPickerEmployee(item: NameSource): PickerEmployee {
  return {
    employeeId: item.employeeId,
    employeeNumber: item.employeeNumber ?? null,
    displayName: employeeDisplayName(item),
    username: item.username ?? null,
  };
}

function mergeEmployee(
  existing: PickerEmployee | undefined,
  next: PickerEmployee,
): PickerEmployee {
  if (!existing) return next;
  const preferNextName =
    !looksLikeEmployeeNumber(next.displayName, next.employeeNumber) &&
    looksLikeEmployeeNumber(existing.displayName, existing.employeeNumber);
  return {
    employeeId: next.employeeId,
    employeeNumber: next.employeeNumber || existing.employeeNumber,
    displayName: preferNextName ? next.displayName : existing.displayName || next.displayName,
    username: next.username || existing.username,
  };
}

export function HolidayCalendarFormScreen(props: Props) {
  const palette = useAppColors();
  const isEdit = isEditRoute(props);
  const navigation = props.navigation;
  const holidayCalendarId = isEdit ? props.route.params.holidayCalendarId : null;

  const [calendarCode, setCalendarCode] = useState('');
  const [calendarName, setCalendarName] = useState('');
  const [description, setDescription] = useState('');
  const [calendarYear, setCalendarYear] = useState(
    String(Number(todayBusinessCalendarDate().slice(0, 4))),
  );
  const [isDefault, setIsDefault] = useState(false);
  const [employeeIds, setEmployeeIds] = useState<number[]>([]);
  const [employees, setEmployees] = useState<PickerEmployee[]>([]);
  const [assignments, setAssignments] = useState<EmployeeCalendarAssignment[]>([]);
  const [rowVersion, setRowVersion] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [assignmentRows, employeeRows, calendar] = await Promise.all([
        getEmployeeCalendarAssignments().catch(() => [] as EmployeeCalendarAssignment[]),
        getAllOrganizationEmployees().catch(() => [] as EmployeeListItem[]),
        holidayCalendarId != null
          ? getHolidayCalendar(holidayCalendarId)
          : Promise.resolve(null),
      ]);
      setAssignments(assignmentRows);

      const byId = new Map<number, PickerEmployee>();

      for (const item of employeeRows) {
        byId.set(
          item.employeeId,
          toPickerEmployee({
            employeeId: item.employeeId,
            employeeNumber: item.employeeNumber,
            displayName: item.displayName,
            username: item.username,
          }),
        );
      }

      for (const row of assignmentRows) {
        byId.set(
          row.employeeId,
          mergeEmployee(
            byId.get(row.employeeId),
            toPickerEmployee(row),
          ),
        );
      }

      const assignedOnCalendar: HolidayCalendarEmployee[] = calendar?.employees || [];
      for (const emp of assignedOnCalendar) {
        byId.set(
          emp.employeeId,
          mergeEmployee(
            byId.get(emp.employeeId),
            toPickerEmployee(emp),
          ),
        );
      }

      setEmployees(
        [...byId.values()].sort((a, b) =>
          a.displayName.localeCompare(b.displayName),
        ),
      );

      if (calendar) {
        setCalendarCode(calendar.calendarCode);
        setCalendarName(calendar.calendarName);
        setDescription(calendar.description || '');
        setCalendarYear(String(calendar.calendarYear));
        setIsDefault(calendar.isDefault);
        setRowVersion(calendar.rowVersion);
        // Keep previously assigned employees selected on Edit.
        const selected = assignedOnCalendar
          .map(emp => emp.employeeId)
          .filter(id => Number.isFinite(id) && id > 0);
        setEmployeeIds(selected);
      }
    } catch (caught) {
      setError(normalizeApiError(caught).message);
    } finally {
      setLoading(false);
    }
  }, [holidayCalendarId]);

  useEffect(() => {
    load().catch(() => undefined);
  }, [load]);

  const employeeOptions = useMemo(() => {
    const selectedSet = new Set(employeeIds);
    return employees.map(item => {
      const assignment = assignments.find(
        row => row.employeeId === item.employeeId,
      );
      const assignedToThis =
        selectedSet.has(item.employeeId) ||
        (assignment?.holidayCalendarId != null &&
          assignment.holidayCalendarId === holidayCalendarId);
      const assignedElsewhere =
        assignment?.holidayCalendarId != null &&
        assignment.holidayCalendarId !== holidayCalendarId &&
        !selectedSet.has(item.employeeId);

      const numberPart = item.employeeNumber
        ? `Emp #${item.employeeNumber}`
        : null;

      let subtitle: string;
      let badge: string | null = null;
      let disabled = false;

      if (assignedElsewhere) {
        subtitle = [
          numberPart,
          `Assigned to ${assignment?.calendarName || 'another calendar'}`,
        ]
          .filter(Boolean)
          .join(' · ');
        badge = 'Assigned elsewhere';
        disabled = true;
      } else if (assignedToThis) {
        subtitle = [numberPart, 'Assigned to this calendar']
          .filter(Boolean)
          .join(' · ');
        badge = 'Selected';
      } else {
        subtitle = [numberPart, 'Not assigned'].filter(Boolean).join(' · ');
      }

      return {
        id: item.employeeId,
        label: item.displayName,
        subtitle,
        badge,
        disabled,
      };
    });
  }, [assignments, employeeIds, employees, holidayCalendarId]);

  async function onSave() {
    setFieldError(null);
    const name = calendarName.trim();
    const year = Number(calendarYear);
    if (!name) {
      setFieldError('Calendar name is required.');
      return;
    }
    if (!Number.isInteger(year) || year < 2000 || year > 2200) {
      setFieldError('Enter a valid calendar year.');
      return;
    }

    setSaving(true);
    try {
      if (isEdit && holidayCalendarId != null) {
        await runOperation('update', () => updateHolidayCalendar(holidayCalendarId, {
          calendarName: name,
          description: description.trim() || null,
          calendarYear: year,
          isDefault,
          isActive: true,
          rowVersion,
          employeeIds,
        }));
      } else {
        await runOperation('add', () => createHolidayCalendar({
          calendarCode: calendarCode.trim() || null,
          calendarName: name,
          description: description.trim() || null,
          calendarYear: year,
          isDefault,
          employeeIds,
        }));
      }
      navigation.navigate(routes.holidayCalendarList);
    } catch (caught) {
      setFieldError(normalizeApiError(caught).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <AppScreen webLayout="narrow" keyboardSafe contentStyle={styles.screen}>
      <ScreenHeader
        title={isEdit ? 'Edit calendar' : 'Create calendar'}
        onBack={navigation.goBack}
      />
      <ScrollView
        contentContainerStyle={styles.content}
        keyboardShouldPersistTaps="handled"
        nestedScrollEnabled
      >
        {error ? (
          <Text accessibilityRole="alert" style={[styles.error, { color: palette.error }]}>
            {error}
          </Text>
        ) : null}
        {!isEdit ? (
          <AppInput
            label="Calendar code (optional)"
            value={calendarCode}
            onChangeText={setCalendarCode}
            placeholder="Auto-generated from name if empty"
            autoCapitalize="characters"
          />
        ) : (
          <View>
            <Text style={[styles.label, { color: palette.textSecondary }]}>Calendar code</Text>
            <Text style={[styles.value, { color: palette.textPrimary }]}>
              {calendarCode || '—'}
            </Text>
          </View>
        )}
        <AppInput
          label="Calendar name"
          value={calendarName}
          onChangeText={setCalendarName}
          placeholder="e.g. Corporate calendar"
        />
        <AppInput
          label="Description"
          value={description}
          onChangeText={setDescription}
          placeholder="Optional notes"
        />
        <AppInput
          label="Calendar year"
          value={calendarYear}
          onChangeText={setCalendarYear}
          keyboardType="number-pad"
        />
        <View style={styles.switchRow}>
          <Text style={[styles.switchLabel, { color: palette.textPrimary }]}>
            Default calendar for unassigned employees
          </Text>
          <Switch value={isDefault} onValueChange={setIsDefault} />
        </View>
        <MultiSelectEntityPicker
          label="Employees"
          options={employeeOptions}
          selectedIds={employeeIds}
          onChange={setEmployeeIds}
          emptyLabel="Select one or more employees"
          accessibilityLabel="Select employees"
          defaultOpen={isEdit}
          disabled={loading}
        />
        {employeeIds.length > 0 ? (
          <Text style={[styles.hint, { color: palette.textSecondary }]}>
            {employeeIds.length} employee
            {employeeIds.length === 1 ? '' : 's'} selected
            {isEdit ? ' (includes previously assigned)' : ''}
          </Text>
        ) : null}
        <Text style={[styles.hint, { color: palette.textSecondary }]}>
          An employee can belong to only one calendar. Employees already on
          another calendar are locked until removed from that calendar.
        </Text>
        {fieldError ? (
          <Text accessibilityRole="alert" style={[styles.error, { color: palette.error }]}>
            {fieldError}
          </Text>
        ) : null}
        <AppButton
          title={isEdit ? 'Save calendar' : 'Create calendar'}
          onPress={() => {
            onSave().catch(() => undefined);
          }}
          loading={saving || loading}
          disabled={saving || loading}
        />
      </ScrollView>
    </AppScreen>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1 },
  content: { padding: spacing.md, gap: spacing.md, paddingBottom: spacing.xl },
  label: { ...typography.caption, fontWeight: '600', marginBottom: spacing.xs },
  value: { ...typography.body },
  switchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
  },
  switchLabel: { ...typography.body, flex: 1, paddingRight: spacing.md },
  hint: { ...typography.caption },
  error: { ...typography.caption },
});
